import { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { buyNewBadge, createBadgePaymentIntent } from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';
import BadgeCheckoutForm from './BadgeCheckoutForm';

import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Checkbox,
	FormControlLabel,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import './AuditoriumBadgeDialog.scss';
import badgeLogo from './AIFG_Badge.svg';

const AuditoriumBadgeDialog = ({
	toggleBadgeDialog,
	open,
	eventId,
	auditoriumId,
	timeslot,
	userId,
	onBadgeIssued,
	eventSlug,
}) => {
	const dispatch = useDispatch();
	const translation = useSelector(
		(state) => state.languages.translations[state.languages.platformLanguage]
	);

	const [badgeIssued, setBadgeIssued] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [confirmNameChecked, setConfirmNameChecked] = useState(false);

	// Stripe state
	const [clientSecret, setClientSecret] = useState(null);
	const [showPaymentForm, setShowPaymentForm] = useState(false);
	const [paymentIntentId, setPaymentIntentId] = useState(null);

	const stripePromise = useMemo(
		() => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY),
		[]
	);

	const handleClose = (_, reason) => {
		// Prevent closing on backdrop click
		if (reason === 'backdropClick') {
			return;
		}

		toggleBadgeDialog();
		setErrorMsg('');
		setIsLoading(false);
		setClientSecret(null);
		setShowPaymentForm(false);
		setBadgeIssued(false);
		setConfirmNameChecked(false);
	};

	const createPaymentIntent = useCallback(async () => {
		if (!eventId) throw new Error('Missing eventId in route or props.');
		setIsLoading(true);
		try {
			const paymentIntent = await dispatch(
				createBadgePaymentIntent(eventId, {
					userId,
					productId: process.env.REACT_APP_STRIPE_BADGE_PRODUCT_ID,
				})
			);
			setClientSecret(paymentIntent.client_secret);
			setPaymentIntentId(paymentIntent.id);
			setShowPaymentForm(true);
			setErrorMsg('');
		} catch (e) {
			setErrorMsg(e?.message || 'Error creating payment.');
		} finally {
			setIsLoading(false);
		}
	}, [dispatch, eventId, userId]);

	const issueBadgeAfterPayment = useCallback(async () => {
		// issue the badge via existing action (talks to /event/:eventId/badge)
		const badgeData = {
			name: timeslot?.title,
			event: eventId,
			timeslot,
			auditorium: auditoriumId,
			user: userId,
			stripePaymentIntentId: paymentIntentId,
		};
		await dispatch(buyNewBadge(badgeData));
	}, [dispatch, auditoriumId, eventId, timeslot, userId, paymentIntentId]);

	const handlePaymentSuccess = async () => {
		try {
			setIsLoading(true);
			await issueBadgeAfterPayment();
			setBadgeIssued(true);
			setErrorMsg('');
			// after success, hide the payment form
			setShowPaymentForm(false);
			setClientSecret(null);

			if (typeof onBadgeIssued === 'function') {
				onBadgeIssued();
			}
		} catch (e) {
			if (e?.code === 'BADGE_ALREADY_OWNED') {
				// Badge already exists for this session: treat as successful for UX purposes
				setBadgeIssued(true);
				setErrorMsg('');
				setShowPaymentForm(false);
				setClientSecret(null);

				if (typeof onBadgeIssued === 'function') {
					onBadgeIssued();
				}
			} else {
				// Payment succeeded but badge issuing failed
				setErrorMsg(
					e?.message ||
						'Payment succeeded, but issuing the badge failed. Please contact support.'
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handlePaymentError = (msg) => setErrorMsg(msg || 'Payment failed.');

	const handleBadgePurchaseClick = async () => {
		if (!confirmNameChecked) {
			setErrorMsg('Please confirm your name before proceeding.');
			return;
		}
		setErrorMsg('');
		await createPaymentIntent();
	};

	const elementsOptions = useMemo(
		() => ({
			clientSecret,
			appearance: {
				theme: 'flat',
				variables: {
					colorPrimary: '#fbd207',
					colorPrimaryText: '#2f4f73',
					colorBackground: '#ffffff',
					colorText: '#30313d',
					colorTextSecondary: '#6b7280',
					colorDanger: '#c0392b',
					borderRadius: '8px',
					spacingUnit: '3px',
					fontFamily: '"Work Sans", "Segoe UI", system-ui, sans-serif',
					fontSizeBase: '15px',
				},
				rules: {
					'.Input': {
						borderColor: '#cbd5e1',
						borderWidth: '1px',
						boxShadow: 'none',
						padding: '14px 16px',
						color: '#111827',
					},
					'.Input:hover': {
						borderColor: '#94a3b8',
					},
					'.Input:focus': {
						borderColor: '#fbd207',
						boxShadow: '0 0 0 1px #fbd207',
					},
					'.Input--invalid': {
						borderColor: '#dc2626',
					},
					'.Label': {
						color: '#6b7280',
						fontSize: '13px',
						fontWeight: '500',
					},
					'.Tab': {
						padding: '12px 16px',
						border: '1px solid #cbd5e1',
						borderRadius: '8px',
						color: '#374151',
					},
					'.Tab:hover': {
						borderColor: '#94a3b8',
					},
					'.Tab--selected': {
						borderColor: '#fbd207',
						color: '#30313d',
					},
					'.Block': {
						padding: '12px 0',
					},
				},
				labels: 'floating',
				fonts: [
					{
						cssSrc:
							'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600&display=swap',
					},
				],
			},
			// Enable billing address collection
			paymentMethodCreation: 'manual',
		}),
		[clientSecret]
	);

	return open ? (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			classes={{ paper: 'dialog-paper' }}
		>
			<DialogTitle disableTypography className="dialog-title">
				{badgeIssued
					? translation?.auditorium?.claimYourBadgeDialogTitleSuccess
					: translation?.auditorium?.claimYourBadgeDialogTitle}
				<Close
					onClick={handleClose}
					classes={{
						root: 'grey-color close-icon',
					}}
				/>
			</DialogTitle>

			<DialogContent className="dialog-content">
				<Typography className="dialog-text">
					{badgeIssued
						? translation?.auditorium?.claimYourBadgeDialogSuccessDescription
						: translation?.auditorium?.claimYourBadgeDialogDescription}
				</Typography>

				{/* Badge card */}
				<Box className="badge-wrap">
					<img className="badge-logo" src={badgeLogo} alt="AI for Good badge" />
					{!badgeIssued && !showPaymentForm && (
						<span className="price-pill">CHF10</span>
					)}
				</Box>

				{/* Confirm text */}
				{!badgeIssued && !showPaymentForm && (
					<Box className="confirm-row">
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={confirmNameChecked}
									onChange={(e) => setConfirmNameChecked(e.target.checked)}
								/>
							}
							label={
								<span>
									{
										translation?.auditorium
											?.claimYourBadgeDialogNameConfirmation
									}{' '}
									<Link
										to={`/event/${eventSlug}/my-account`}
										target="_blank"
										rel="noopener noreferrer"
										style={{ color: 'inherit', fontWeight: 'bold' }}
									>
										{
											translation?.auditorium
												?.claimYourBadgeDialogNameConfirmationLink
										}
									</Link>
									.
								</span>
							}
						/>
					</Box>
				)}

				{/* Stripe payment form */}
				{!badgeIssued && showPaymentForm && clientSecret && (
					<Elements stripe={stripePromise} options={elementsOptions}>
						<BadgeCheckoutForm
							onSuccess={handlePaymentSuccess}
							onError={handlePaymentError}
							afterSubmit={() => {}}
							errorMsg={errorMsg}
						/>
					</Elements>
				)}
			</DialogContent>

			<DialogActions className="dialog-actions">
				{badgeIssued && (
					<Button className="close-btn" onClick={handleClose}>
						{translation?.auditorium?.claimYourBadgeDialogCloseButton}
					</Button>
				)}
				{badgeIssued && (
					<Button
						className="action-btn"
						component={Link}
						to={{
							pathname: `/event/${eventSlug}/my-account`,
							state: { selectedTab: 'credentials' },
						}}
						onClick={handleClose}
					>
						{translation?.auditorium?.claimYourBadgeDialogMyCredentialsButton}
					</Button>
				)}
				{!badgeIssued && !showPaymentForm && (
					<Button
						className="action-btn"
						onClick={handleBadgePurchaseClick}
						disabled={isLoading || !confirmNameChecked}
					>
						{isLoading
							? translation?.auditorium?.claimYourBadgeDialogPaymentProcessing
							: `${translation?.auditorium?.claimYourBadgeDialogPaymentButton} CHF10`}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	) : null;
};

export default AuditoriumBadgeDialog;
